import { selectWarehouse, updateWarehouse } from 'containers/Warehouses/warehousesSlice'
import { useReduxDispatch, useReduxSelector } from 'store'
import { useHistory, useParams } from 'react-router-dom'
import React, { useState } from 'react'
import { Step } from '../core/utils'
import { POST } from 'rest/request'
import * as urls from 'rest/urls'

const useResignationAction = (
  type: warehouse.ResignationType,
  selectedData: payments.SelectedData,
  delivery: payments.Delivery[],
  setStep: React.Dispatch<React.SetStateAction<Step>>
) => {
  const [isSending, setSending] = useState(false)
  const { id } = useParams<warehouse.PageParams>()
  const warehouse = useReduxSelector(state => selectWarehouse(state, id))

  const { goBack } = useHistory()
  const dispatch = useReduxDispatch()
  /**
   * settlement=payNow (tylko nowa rezygnacja): po zapisie NIE nawigujemy - wywolujacy
   * dostaje termination.payNow i przechodzi do platnosci.
   */
  const sendResignation = async (settlement?: payments.TerminationSettlement): Promise<ResignationResult> => {
    if (isSending) return { ok: false, skipped: true }
    setSending(true)
    try {
      const url = warehouse.resignation?.id
        ? `${urls.contractresignations}/${warehouse.resignation.id}`
        : urls.contractResignation(warehouse.contractId)
      const formData = new FormData()
      formData.append('date', selectedData.date)
      formData.append('type', type)
      if (settlement && !warehouse.resignation?.id) {
        formData.append('settlement', settlement)
      }
      if (selectedData.reasonId) {
        formData.append('reasonId', selectedData.reasonId)
      }
      if (selectedData.reason) {
        formData.append('reason', selectedData.reason)
      }
      if (selectedData.refundName) {
        formData.append('refundName', selectedData.refundName)
      }
      if (selectedData.refundAccountNumber) {
        formData.append('refundAccountNumber', selectedData.refundAccountNumber)
      }
      if (selectedData.photos) {
        selectedData.photos.forEach((photo, idx) => {
          formData.append(`photo[${idx}]`, photo)
        })
      }
      if (selectedData.itemsToReturn) {
        Object.entries(selectedData.itemsToReturn).forEach(([itemId, deliveryId]) => {
          formData.append(`itemsToReturn[${itemId}]`, deliveryId)
        })
        if (delivery) {
          const generateFormDataFromObject = (baseKey: string, object: Object) => {
            Object.entries(object).forEach(([key, value]) => {
              const currentKey = `${baseKey}[${key}]`
              if (typeof value === 'object') {
                generateFormDataFromObject(currentKey, value)
              } else {
                formData.append(currentKey, value)
              }
            })
          }
          delivery.forEach((delivery, idx) => {
            generateFormDataFromObject(`delivery[${idx}]`, delivery)
          })
        }
      }
      const response: payments.ResignationResponse = await POST(url, { form: formData })
      setSending(false)
      if (response.data) {
        const termination = getTermination(response)
        const keyReturn = getKeyReturn(response)
        dispatch(updateWarehouse({
          id,
          values: {
            resignation: response.data,
            // rentedreserved zwraca keyReturn dopiero przy nastepnym pobraniu listy - uzupelniamy od razu
            ...(keyReturn ? { keyReturn } : {})
          }
        }))
        // "Zaplac teraz" albo obowiazkowa faktura "teraz" (payNow.requested: false) - kazda odpowiedz
        // z niepustym termination.payNow.documents konczy sie pollingiem i platnoscia (dok. 2026-10-07)
        if (settlement === 'payNow' || (termination?.payNow?.documents?.length ?? 0) > 0) {
          return { ok: true, termination, awaitingPayment: true, canFinish: response.data.canFinish }
        }
        if (response.data.canFinish) setStep('finish')
        else goBack()
        return { ok: true, termination }
      }
      // HTTP 200 z {"error": "..."} - rozgaleziamy po kluczu error, nie po statusie
      return { ok: false, message: response.error }
    } catch (error) {
      // blad walidacji (np. 422 z errors.date) - komunikat pokazuje juz rest (meta.custom.notification)
      setSending(false)
      return { ok: false, ...parseResignationError(error) }
    }
  }

  return {
    isSending,
    sendResignation
  }
}

export interface ResignationResult {
  ok: boolean
  /** wysylka juz trwa - nic nie wyslano */
  skipped?: boolean
  message?: string
  errors?: Record<string, string[]>
  /** jest dokument do zaplaty (payNow) - wywolujacy przechodzi do platnosci (hook nie nawigowal) */
  awaitingPayment?: boolean
  /** zakonczenie dopelnione (np. ze zdjeciem) - po platnosci ekran Final */
  canFinish?: boolean
  /** blok termination z odpowiedzi zapisu (m.in. payNow) */
  termination?: payments.ResignationResponseTermination
}

/** request() odrzuca promise z JSON.stringify(body) bledu */
const parseResignationError = (error: unknown): Omit<ResignationResult, 'ok'> => {
  try {
    const data = JSON.parse(String(error))
    return {
      message: typeof data?.message === 'string' ? data.message : undefined,
      errors: data?.errors && typeof data.errors === 'object' && !Array.isArray(data.errors) ? data.errors : undefined
    }
  } catch {
    return {}
  }
}

const getTermination = (response: payments.ResignationResponse) =>
  response.termination ?? response.data?.termination

/** termination.keyReturn i termination.quote.keyReturn niosa te same dane */
const getKeyReturn = (response: payments.ResignationResponse): warehouse.KeyReturn | undefined => {
  const termination = getTermination(response)
  return termination?.keyReturn ?? termination?.quote?.keyReturn
}

export default useResignationAction